/**
 * Sources — Plan 07-04 Task 2 GREEN.
 *
 * Closes ABOUT-03: machine-readable sources.json rendered as a human-browsable
 * list with filter-by-type, filter-by-archive, free-text search, and URL-hash
 * anchor deep-linking to specific records (enables Plan 07-03's ReadTheTour +
 * the in-Canvas SourcesPanel to link into specific records via
 * /sources#<record-id>).
 *
 * Data: fetched via `useSourcesIndex` from /manifest/sources.json (92 records
 * at launch). Filter chips + searchbox update local filter state via the hook.
 *
 * Anchor deep-link: on mount (after load), reads `window.location.hash`, looks
 * up the DOM element with that id, and calls scrollIntoView. The <li> for each
 * record has id={record.id} so any record can be linked as `/sources#<id>`.
 * About.tsx's Klan paragraph link uses this mechanism to jump to the
 * about04-klan-rally-edgewood-1923-09-11 record.
 *
 * AESTHETIC-BRIEF Rule 7 chrome via shared PeriodChromeStyles tokens.
 */
import { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useSourcesIndex } from '../content/useSourcesIndex';
import { typography, periodChrome } from '../ui/PeriodChromeStyles';

export function Sources() {
  const { filtered, loading, filter, setFilter, records, byType, byArchive } =
    useSourcesIndex();
  const location = useLocation();

  // Anchor deep-link: after records load, scroll to #<id> if present in URL.
  // Reads from react-router's location.hash (works in MemoryRouter during tests
  // and BrowserRouter in production) with window.location.hash as a fallback
  // for direct-page-load scenarios.
  useEffect(() => {
    if (loading) return;
    if (records.length === 0) return;
    const routerHash = location.hash?.replace(/^#/, '') ?? '';
    const windowHash = (typeof window !== 'undefined' ? window.location.hash : '').replace(
      /^#/,
      '',
    );
    const hash = routerHash || windowHash;
    if (!hash) return;
    const el = document.getElementById(hash);
    if (el) {
      // Scroll directly after render — the useEffect already runs after layout
      // commit, and rAF adds a tick that happy-dom doesn't drive in tests.
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [loading, records.length, location.hash]);

  const totalRecords = records.length;

  return (
    <main role="main" aria-labelledby="sources-title" style={S.main}>
      <div style={S.inner}>
        <header style={S.header}>
          <nav aria-label="Site navigation" style={S.nav}>
            <Link to="/" style={S.navLink}>
              Experience
            </Link>
            <span style={S.navSep} aria-hidden="true">·</span>
            <Link to="/about" style={S.navLink}>
              About
            </Link>
            <span style={S.navSep} aria-hidden="true">·</span>
            <Link to="/credits" style={S.navLink}>
              Credits
            </Link>
          </nav>
          <h1 id="sources-title" style={S.h1}>
            Sources
          </h1>
          <p style={S.subtitle}>
            {loading
              ? 'Loading sources…'
              : `${filtered.length} of ${totalRecords} records.`}
          </p>
        </header>

        <section aria-labelledby="filters-heading" style={S.filters}>
          <h2 id="filters-heading" style={S.h2}>
            Filters
          </h2>
          <input
            type="search"
            role="searchbox"
            aria-label="Search source titles and rationales"
            placeholder="Search titles and rationales…"
            value={filter.search}
            onChange={(e) => setFilter({ search: e.target.value })}
            style={S.search}
          />
          <div style={S.chipRow} role="group" aria-label="Filter by type">
            <button
              type="button"
              onClick={() => setFilter({ type: null })}
              style={filter.type === null ? S.chipActive : S.chip}
            >
              All types {totalRecords ? `(${totalRecords})` : ''}
            </button>
            {Object.entries(byType)
              .sort((a, b) => b[1] - a[1])
              .map(([t, n]) => (
                <button
                  key={t}
                  type="button"
                  onClick={() => setFilter({ type: t })}
                  style={filter.type === t ? S.chipActive : S.chip}
                >
                  {t} ({n})
                </button>
              ))}
          </div>
          <div style={S.chipRow} role="group" aria-label="Filter by archive">
            <button
              type="button"
              onClick={() => setFilter({ archive: null })}
              style={filter.archive === null ? S.chipActive : S.chip}
            >
              All archives
            </button>
            {Object.entries(byArchive)
              .sort((a, b) => b[1] - a[1])
              .slice(0, 12)
              .map(([a, n]) => (
                <button
                  key={a}
                  type="button"
                  onClick={() => setFilter({ archive: a })}
                  style={filter.archive === a ? S.chipActive : S.chip}
                >
                  {a} ({n})
                </button>
              ))}
          </div>
        </section>

        <ul style={S.list}>
          {filtered.map((r) => (
            <li key={r.id} id={r.id} data-source-id={r.id} style={S.item} tabIndex={-1}>
              <h3 style={S.itemTitle}>{r.title}</h3>
              <p style={S.meta}>
                <span>{r.type}</span>
                {r.confidence_tier ? (
                  <>
                    {' · '}
                    <span>{r.confidence_tier}</span>
                  </>
                ) : null}
                {r.date ? (
                  <>
                    {' · '}
                    <span>{r.date}</span>
                  </>
                ) : null}
              </p>
              <p style={S.attribution}>{r.attribution}</p>
              {r.how_we_know ? <p style={S.hwk}>{r.how_we_know}</p> : null}
              <p style={S.links}>
                <a href={r.urls.live} target="_blank" rel="noreferrer" style={S.link}>
                  Live source
                </a>
                {r.urls.wayback ? (
                  <>
                    {' · '}
                    <a href={r.urls.wayback} target="_blank" rel="noreferrer" style={S.link}>
                      Wayback snapshot
                    </a>
                  </>
                ) : null}
                {' · '}
                <a
                  href={r.rights.statement_uri}
                  target="_blank"
                  rel="noreferrer"
                  style={S.rightsLink}
                  title={r.rights.statement_label}
                >
                  {r.rights.statement_label || 'Rights'}
                </a>
              </p>
            </li>
          ))}
        </ul>
      </div>
    </main>
  );
}

const S: Record<string, React.CSSProperties> = {
  main: {
    minHeight: '100vh',
    background: periodChrome.cream,
    color: periodChrome.espresso,
    fontFamily: typography.serif,
    padding: '3rem 1.5rem',
    boxSizing: 'border-box',
    lineHeight: 1.55,
  },
  inner: {
    maxWidth: '82ch',
    margin: '0 auto',
  },
  header: {
    marginBottom: '1.5rem',
    borderBottom: `1px solid ${periodChrome.ochre}`,
    paddingBottom: '1rem',
  },
  nav: {
    marginBottom: '1rem',
    fontSize: '0.95em',
    opacity: 0.85,
  },
  navLink: {
    color: periodChrome.espresso,
    textDecoration: 'underline',
    textDecorationColor: periodChrome.ochre,
  },
  navSep: {
    margin: '0 0.5rem',
    color: periodChrome.ochre,
  },
  h1: {
    fontWeight: 400,
    fontSize: '2.25rem',
    letterSpacing: '0.04em',
    margin: '0.5rem 0',
    lineHeight: 1.2,
  },
  subtitle: {
    fontStyle: 'italic',
    opacity: 0.75,
    margin: '0.25rem 0 0',
    fontSize: '0.95rem',
  },
  filters: {
    marginBottom: '1.5rem',
  },
  h2: {
    fontWeight: 400,
    fontSize: '1.1rem',
    margin: '0.5rem 0',
    letterSpacing: '0.02em',
  },
  search: {
    width: '100%',
    padding: '0.5rem 0.75rem',
    border: `1px solid ${periodChrome.ochre}`,
    background: periodChrome.cream,
    color: periodChrome.espresso,
    fontFamily: typography.serif,
    fontSize: '1rem',
    marginBottom: '0.75rem',
    boxSizing: 'border-box',
  },
  chipRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '0.4rem',
    marginBottom: '0.5rem',
  },
  chip: {
    padding: '0.3rem 0.7rem',
    border: `1px solid ${periodChrome.ochreDim}`,
    background: 'transparent',
    cursor: 'pointer',
    fontFamily: typography.serif,
    color: periodChrome.espresso,
    fontSize: '0.9rem',
  },
  chipActive: {
    padding: '0.3rem 0.7rem',
    border: `1px solid ${periodChrome.ochre}`,
    background: periodChrome.ochreDim,
    cursor: 'pointer',
    fontFamily: typography.serif,
    color: periodChrome.espresso,
    fontWeight: 600,
    fontSize: '0.9rem',
  },
  list: {
    listStyle: 'none',
    padding: 0,
    margin: 0,
  },
  item: {
    marginBottom: '1.5rem',
    paddingBottom: '1rem',
    borderBottom: `1px solid ${periodChrome.ochreDim}`,
  },
  itemTitle: {
    fontSize: '1.1rem',
    margin: '0 0 0.25rem',
    fontWeight: 600,
  },
  meta: {
    fontSize: '0.9em',
    opacity: 0.75,
    margin: '0.15rem 0',
  },
  attribution: {
    fontSize: '0.95em',
    margin: '0.35rem 0',
  },
  hwk: {
    fontStyle: 'italic',
    margin: '0.5rem 0',
    color: periodChrome.warmOchre,
  },
  links: {
    fontSize: '0.9em',
    margin: '0.5rem 0 0',
  },
  link: {
    color: periodChrome.espresso,
    textDecoration: 'underline',
    textDecorationColor: periodChrome.ochre,
  },
  rightsLink: {
    color: periodChrome.espresso,
    textDecoration: 'underline',
    textDecorationColor: periodChrome.ochre,
    opacity: 0.85,
  },
};
